import { useEffect, useRef, useState } from "react";

export type Message = {
  text: string;
  sender: "sent" | "received";
  timestamp: number;
};

type ChatDrawerProps = {
  isOpen: boolean;
  peerId: string | null;
  displayName: string;
  deviceName: string;
  messages: Message[];
  onClose: () => void;
  onSendMessage: (text: string) => void;
  onClearHistory: () => void;
};

export function ChatDrawer({
  isOpen,
  peerId,
  displayName,
  deviceName,
  messages,
  onClose,
  onSendMessage,
  onClearHistory,
}: ChatDrawerProps) {
  const [inputText, setInputText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-focus input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 150);
    }
  }, [isOpen, peerId]);

  // Auto-scroll to bottom on messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Auto-grow input height
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = `${ta.scrollHeight}px`;
    }
  }, [inputText]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    onSendMessage(inputText.trim());
    setInputText("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  };

  const renderMessageText = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts = text.split(urlRegex);
    if (parts.length === 1) return text;

    return parts.map((part, index) => {
      if (urlRegex.test(part) || (part.startsWith("www.") && !part.includes(" "))) {
        let href = part;
        if (part.startsWith("www.")) {
          href = "http://" + part;
        }
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-indigo-200 break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div
      className={`fixed top-0 right-0 z-50 flex h-full w-full flex-col bg-[var(--surface)]/95 backdrop-blur-md shadow-2xl transition-transform duration-300 ease-in-out border-l border-[var(--border)] sm:w-96 ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
        <div className="flex flex-col min-w-0">
          <span className="font-semibold truncate text-[var(--text)]">{displayName}</span>
          <span className="text-xs text-[var(--muted)] truncate">
            {deviceName} · {peerId ? "connected" : "disconnected"}
          </span>
        </div>

        <div className="flex gap-2">
          {/* Clear History */}
          <button
            type="button"
            onClick={onClearHistory}
            className="p-2 text-[var(--muted)] hover:text-red-500 hover:bg-red-500/10 rounded-full transition-colors"
            title="Clear Chat History"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>

          {/* Close */}
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--border)] rounded-full transition-colors"
            title="Close Chat"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center text-sm text-[var(--muted)] p-6">
            <span className="text-2xl mb-2">💬</span>
            <p>No messages yet.</p>
            <p className="text-xs mt-1">Send a message to start chatting!</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isSent = msg.sender === "sent";
            return (
              <div
                key={index}
                className={`flex flex-col max-w-[80%] ${
                  isSent ? "ml-auto items-end" : "mr-auto items-start"
                }`}
              >
                <div
                  className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm ${
                    isSent
                      ? "bg-[var(--color-accent)] text-white rounded-br-none"
                      : "bg-[var(--border)] text-[var(--text)] rounded-bl-none"
                  }`}
                >
                  {renderMessageText(msg.text)}
                </div>
                <span className="text-[10px] text-[var(--muted)] mt-1 px-1">
                  {formatTime(msg.timestamp)}
                </span>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Footer / Input */}
      <div className="border-t border-[var(--border)] p-3 bg-[var(--surface)]">
        <div className="flex items-end gap-2 bg-[var(--bg)] border border-[var(--border)] rounded-xl px-3 py-2 focus-within:border-[var(--color-accent)] focus-within:ring-1 focus-within:ring-[var(--color-accent)] transition-all">
          <textarea
            ref={textareaRef}
            rows={1}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={peerId ? "Type a message..." : "Peer disconnected"}
            disabled={!peerId}
            className="flex-1 max-h-32 bg-transparent text-sm resize-none focus:outline-none focus:ring-0 placeholder-[var(--muted)] py-1"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!inputText.trim() || !peerId}
            className="p-1.5 rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-strong)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Send Message"
          >
            <svg className="h-4.5 w-4.5 transform rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
